import React from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { Box } from '@mui/material';
import { formatDate } from '../../dashboard/components/dateUtils';
import { parseRawFormData } from '../../dashboard/components/formUtils';
import { HandlersPnrResponse } from '../../app/athenaApiGen';
import {
  WorkflowProgressValue,
  getWorkflowProgress,
} from '../../utils/workflow';

// Extended type to include fields that may be present in API response but not in generated types
type PNRApiItem = HandlersPnrResponse & {
  projectName?: string;
  createdAt?: string;
  workflowState?: string;
  workflowProgress?: WorkflowProgressValue;
};

export interface PNRRowData {
  id: string;
  pnrNumber: string;
  projectName: string;
  problemSummary: string;
  impactDelivery: string;
  actionRequired: string;
  createdAt: string | null;
  recoverable: string;
  costsAnticipated: string;
  workflowState: string | null;
  workflowProgress: WorkflowProgressValue;
  workflowId: string | null;
}

const FALLBACK_TEXT = '--';

/**
 * Returns the first non-empty string encountered in the provided values.
 */
const pickFirstString = (
  ...values: Array<string | null | undefined>
): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value;
  }
  return undefined;
};

export const createPNRColumns = (): GridColDef<PNRRowData>[] => [
  { 
    field: 'pnrNumber', 
    headerName: 'PNR Number', 
    minWidth: 120, 
    flex: 0.7 
  },
  { 
    field: 'problemSummary', 
    headerName: 'Problem Description', 
    minWidth: 300, 
    flex: 2,
    renderCell: (params) => (
      <Box sx={{ 
        overflow: 'hidden', 
        textOverflow: 'ellipsis', 
        whiteSpace: 'nowrap',
        width: '100%',
        py: 1
      }}>
        {params.value}
      </Box>
    ),
  },
  { 
    field: 'impactDelivery', 
    headerName: 'Impacts Delivery', 
    minWidth: 110, 
    flex: 0.6,
    align: 'center',
    headerAlign: 'center',
  },
  { 
    field: 'actionRequired', 
    headerName: 'Gov Action', 
    minWidth: 90, 
    flex: 0.5,
    align: 'center',
    headerAlign: 'center',
  },
  { 
    field: 'recoverable', 
    headerName: 'Recoverable', 
    minWidth: 90, 
    flex: 0.5,
    align: 'center',
    headerAlign: 'center',
  },
  { 
    field: 'costsAnticipated', 
    headerName: 'Cost Impact', 
    minWidth: 90, 
    flex: 0.5,
    align: 'center',
    headerAlign: 'center',
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    minWidth: 100,
    flex: 0.6,
    renderCell: (params) => formatDate(params?.value),
    sortable: true,
  },
  {
    field: 'workflowState',
    headerName: 'Status',
    minWidth: 200,
    flex: 0.9,
    sortable: false,
    valueGetter: (value: string) => value ?? '-',
  },
];

export const mapPNRApiDataToRows = (items: PNRApiItem[]): PNRRowData[] => {
  return items.map((it) => {
    const raw = parseRawFormData((it as any).rawFormData);
    const workflowState =
      pickFirstString(
        it.workflowState as string | undefined,
        raw?.workflowState,
        raw?.workflow?.state,
        raw?.workflowStateLabel,
      ) || null;
    const workflowProgress =
      (it.workflowProgress as WorkflowProgressValue | undefined) ??
      (raw?.workflowProgress as WorkflowProgressValue | undefined) ??
      getWorkflowProgress(workflowState ?? undefined);

    const summarySource = pickFirstString(
      it.natureSourceProblemDesc,
      raw?.natureSourceProblemDesc,
    );
    const trimmedSummary = summarySource
      ? summarySource.length > 80
        ? `${summarySource.substring(0, 80)}...`
        : summarySource
      : FALLBACK_TEXT;

    return {
      id: it.pnrId ?? '',
      pnrNumber: pickFirstString(it.pnrNumber, raw?.pnrNumber) ?? FALLBACK_TEXT,
      projectName: pickFirstString(it.projectName, raw?.projectName) ?? FALLBACK_TEXT,
      problemSummary: trimmedSummary,
      impactDelivery:
        it.problemImpactDelivery !== undefined
          ? it.problemImpactDelivery
            ? 'Yes'
            : 'No'
          : raw?.problemImpactDelivery !== undefined
            ? raw.problemImpactDelivery
              ? 'Yes'
              : 'No'
            : FALLBACK_TEXT,
      actionRequired:
        it.actionRequiredByGovernment !== undefined
          ? it.actionRequiredByGovernment
            ? 'Yes'
            : 'No'
          : raw?.actionRequiredByGovernment !== undefined
            ? raw.actionRequiredByGovernment
              ? 'Yes'
              : 'No'
            : FALLBACK_TEXT,
      createdAt: it.dateCreated || it.createdAt || raw?.dateCreated || null,
      recoverable:
        it.deliveryScheduleRecoverable !== undefined
          ? it.deliveryScheduleRecoverable
            ? 'Yes'
            : 'No'
          : raw?.deliveryScheduleRecoverable !== undefined
            ? raw.deliveryScheduleRecoverable
              ? 'Yes'
              : 'No'
            : FALLBACK_TEXT,
      costsAnticipated:
        it.increasedCostAnticipated !== undefined
          ? it.increasedCostAnticipated
            ? 'Yes'
            : 'No'
          : raw?.increasedCostAnticipated !== undefined
            ? raw.increasedCostAnticipated
              ? 'Yes'
              : 'No'
            : FALLBACK_TEXT,
      workflowState,
      workflowProgress,
      workflowId: it.workflowId ?? null,
    };
  });
};